/**
 * Centralized Mock Data Configuration
 *
 * This configuration defines all mock scenarios for the dashboard.
 * Each scenario includes:
 * - id: Unique identifier
 * - keywords: Array of keywords to match queries
 * - enabled: Flag to enable/disable the mock
 * - files: Paths to PDF and PPTX export files
 * - responseFile: Name of the JSON response file
 * - executionPlanItems: Custom items to display in execution plan
 */

export interface ExecutionPlanItem {
  title: string;
  description: string;
}

export interface MockScenario {
  id: string;
  keywords: string[];
  enabled: boolean;
  files: {
    pdf: string;
    powerpoint: string;
  };
  responseFile: string;
  description?: string;
  executionPlanItems?: ExecutionPlanItem[];
}

export const MOCK_SCENARIOS: MockScenario[] = [
  {
    id: 'estisalat-demo',
    keywords: ['produce board'],
    enabled: true,
    files: {
      pdf: '/exports/e-and/Financial-Insights-Analysis-Report.pdf',
      powerpoint: '/exports/e-and/Financial-Insights-Analysis-Report.pptx'
    },
    responseFile: 'e-and.json',
    description: 'E& demo',
    executionPlanItems: [
      { title: 'Financial Performance', description: 'revenue pools, fee/NII mix, unit economics, CTI trajectory, and risk/return (RoRWA) levers' },
      { title: 'Market Intelligence', description: 'size, dynamics, and opportunities' },
    ]
  },
  {
    id: 'strategic-positioning',
    keywords: ['strategic positioning', 'structured positioning analysis'],
    enabled: true,
    files: {
      pdf: '/exports/strategic-positioning/Aspire Bank_Strategic_Positioning.pdf',
      powerpoint: '/exports/strategic-positioning/Aspire_Strategic_Positioning.pptx'
    },
    responseFile: 'strategic-positioning.json',
    description: 'Aspire Bank strategic positioning analysis',
    executionPlanItems: [
      { title: 'Market', description: 'size & growth, demand drivers, barriers, and priority segments' },
      { title: 'Competitive Landscape', description: 'share & positioning, wedge plays, differentiators, and whitespace vs incumbents/fintechs' },
      { title: 'Finance', description: 'revenue pools, fee/NII mix, unit economics, CTI trajectory, and risk/return (RoRWA) levers' },
      { title: 'Regulatory', description: 'licensing pathways, consumer duty & conduct, data/privacy, and instant-payment/open-banking implications' }
    ]
  },
  {
    id: 'hsbc-stablecoin',
    keywords: ['hsbc stablecoin', 'bank stablecoin', 'aspire stablecoin'],
    enabled: true,
    files: {
      pdf: '/exports/hsbc-stablecoin/Stablecoin Launch in the UAE.pdf',
      powerpoint: '/exports/hsbc-stablecoin/Stablecoins_Launch_in_UAE.pptx'
    },
    responseFile: 'hsbc-stablecoin.json',
    description: 'Aspire Bank stablecoin strategy analysis',
    executionPlanItems: [
      { title: 'Market Intelligence', description: 'size, dynamics, and opportunities' },
      { title: 'Customer Insights', description: 'segments, behaviours, and sentiment' },
      { title: 'Trends', description: 'global, regional, and sectoral signals' },
      { title: 'Competitor', description: 'strategies, positioning, strengths, and gaps' },
      { title: 'Technology', description: 'innovation, AI adoption, infrastructure, and emerging capabilities' }
    ]
  },
  {
    id: 'uk-expansion',
    keywords: ['uk expansion', 'uk market'],
    enabled: true,
    files: {
      pdf: '/exports/uk-expansion/UK_Expansion_Aspire Bank_Report.pdf',
      powerpoint: '/exports/uk-expansion/Aspire_UK_Expansion.pptx'
    },
    responseFile: 'uk-expansion.json',
    description: 'Aspire Bank UK market expansion strategy'
  }
];

/**
 * Environment flag to enable/disable mock data system
 * Can be overridden by environment variable
 */
export const MOCK_DATA_ENABLED = process.env.REACT_APP_ENABLE_MOCK_DATA !== 'false';
